import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { formatDate, formatEuro } from "../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Coins, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function CostiDialog({ open, onOpenChange, praticaId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ voce: "", importo: "", data: "" });

  const base = `/studio/pratiche/${praticaId}/costi`;
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(base); setRows(data); } catch (e) { /* */ } finally { setLoading(false); }
  };
  useEffect(() => { if (open) { load(); setForm({ voce: "", importo: "", data: "" }); } /* eslint-disable-next-line */ }, [open]);

  const total = rows.reduce((s, r) => s + Number(r.importo || 0), 0);

  const add = async (e) => {
    e.preventDefault();
    if (!form.voce.trim()) return;
    try { await api.post(base, { voce: form.voce, importo: parseFloat(form.importo) || 0, data: form.data || null }); setForm({ voce: "", importo: "", data: "" }); load(); toast.success("Costo aggiunto"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`${base}/${id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-lg" data-testid="costi-dialog">
        <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><Coins className="h-5 w-5 text-amber-400" /> Costi della pratica</DialogTitle></DialogHeader>
        <form onSubmit={add} className="grid grid-cols-[1fr_120px_auto] gap-2 items-end">
          <div><label className="text-xs text-slate-400">Voce *</label><Input value={form.voce} onChange={(e) => setForm({ ...form, voce: e.target.value })} data-testid="costo-voce" className="mt-1 h-9 bg-slate-900/60 border-slate-700" /></div>
          <div><label className="text-xs text-slate-400">Importo €</label><Input type="number" step="0.01" value={form.importo} onChange={(e) => setForm({ ...form, importo: e.target.value })} data-testid="costo-importo" className="mt-1 h-9 bg-slate-900/60 border-slate-700 font-mono" /></div>
          <Button type="submit" data-testid="costo-add" className="h-9 bg-amber-500 hover:bg-amber-600 text-[#0a0d14]"><Plus className="h-4 w-4" /></Button>
        </form>
        <div className="mt-2 max-h-64 overflow-y-auto divide-y divide-slate-800">
          {loading ? <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-amber-500" /></div>
            : rows.length === 0 ? <div className="py-6 text-center text-sm text-slate-500">Nessun costo.</div>
            : rows.map((c) => (
              <div key={c.id} className="flex items-center justify-between py-2.5" data-testid={`costo-${c.id}`}>
                <div className="min-w-0">
                  <div className="text-sm text-slate-100 truncate">{c.voce}</div>
                  <div className="text-[11px] text-slate-500 font-mono">{formatDate(c.data || c.created_at)}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-mono text-slate-200">{formatEuro(c.importo)}</span>
                  <button onClick={() => del(c.id)} data-testid={`costo-del-${c.id}`} className="text-red-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
        </div>
        <div className="flex items-center justify-between border-t border-slate-800 pt-3">
          <span className="text-xs uppercase tracking-wider text-slate-500 font-mono">Totale</span>
          <span className="text-lg font-head font-extrabold text-emerald-400 font-mono" data-testid="costi-totale">{formatEuro(total)}</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
